import React, { useEffect, useState } from 'react';
import images from '../../constants/images';
import { useTranslation } from 'react-i18next';
import Header from '../../components/header/Header';
import '../../assets/styles/global.css';
import Map from '../../components/map/Map';
import styles from '../about/About.module.css';
import Footer from '../../components/footer/Footer';
import CursorFollower from '../../components/cursor/Cursor';
import { motion } from 'framer-motion';
import '../spinner.css';

const Certification: React.FC = () => {
  const certificate = [
    {
      image: images.a4_1,
      name: 'certification.a4-1',
    },
    {
      image: images.a4_2,
      name: 'certification.a4-2',
    },
    {
      image: images.a4_3,
      name: 'certification.a4-3',
    },
    {
      image: images.a4_4,
      name: 'certification.a4-4',
    },
  ];
  const { t } = useTranslation('global');
  const [isLoading, setIsLoading] = useState(true);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const hasLoadedBefore = sessionStorage.getItem('hasLoadedBefore');

    const timer = setTimeout(
      () => {
        setIsLoading(false);
        sessionStorage.setItem('hasLoadedBefore', 'true'); // mark as loaded
      },
      hasLoadedBefore ? 1000 : 3000,
    ); // shorter delay if already loaded

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > 410); // Show button after scrolling 200px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToContact = (event: any) => {
    event.preventDefault(); // Prevent default anchor behavior
    const headerHeight = document.querySelector('header')?.offsetHeight || 0; // Get header height
    const targetPosition = document.getElementById('bottom')?.offsetTop || 0;

    window.scrollTo({
      top: targetPosition - headerHeight, // Subtract the header height
      behavior: 'smooth', // Smooth scrolling effect
    });
  };

  const title = t('certification.certificationTitle');
  const words = title.split(' ');

  if (isLoading) {
    return (
      <>
        <div className="loader-wrapper">
          <div className="spinner">
            <div className="cube1"></div>
            <div className="cube2"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <CursorFollower />

      {/* Hero Section */}
      <section
        id="hero"
        className="relative top-0 left-0 z-50 font flex flex-col justify-center items-center min-h-screen"
      >
        {/* Header */}
        <Header />

        {/* Background Image */}
        <img
          src={images.certification}
          alt="Certification Background"
          className="absolute inset-0 object-cover w-screen h-full"
        />

        {/* Overlay */}
        <div className="absolute inset-0"></div>

        {/* Content on top of the image */}
        <div
          className={`relative z-10 flex flex-col justify-between items-center sm:text-center text-left text-white px-4 md:px-48 lg:px-52 xl:px-64`}
        >
          {/* Responsive Heading */}
          <h1
            className={`title md:text-center text-4xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl font-bold pb-4 ${styles.head}`}
          >
            {words.map((word, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, y: 50 }} // Start with invisible and slightly below
                animate={{ opacity: 1, y: 0 }} // Animate to fully visible and centered
                transition={{
                  delay: index * 0.2, // Delay each word animation
                  type: 'spring',
                  stiffness: 100,
                  damping: 25,
                }}
                className="inline-block mr-2" // Add some space between words
              >
                {word}
              </motion.span>
            ))}
          </h1>
        </div>

        {/* Scroll Button: Visible only on screens larger than 'lg' */}
        <div className="hidden lg:flex absolute lg:bottom-0 xl:bottom-0 flex-col items-center">
          <a
            href="#bottom"
            onClick={handleScrollToContact}
            className="flex items-center justify-center color text-white  lg:w-[60px] lg:h-[60px] xl:w-[75px] xl:h-[75px] 2xl:w-[105px] 2xl:h-[105px]  hover:bg-red-500 transition duration-300"
          >
            {/* Icon and Text Container */}
            <div className="flex flex-col items-center">
              {/* Arrow Icon */}
              <img
                src={images.down}
                alt="Down Arrow"
                className="w-4 md:w-5 lg:w-5 xl:w-6 2xl:w-7 h-auto mb-1"
              />
              {/* Scroll Text */}
              <span className="text-xs md:text-sm lg:text-sm xl:text-base 2xl:text-lg">
                {t('heroSection.scroll')}
              </span>
            </div>
          </a>
        </div>

        {/* WhatsApp Floating Button */}
        <a
          href="https://wa.me/919909998890"
          target="_blank"
          rel="noopener noreferrer"
          className={`fixed bottom-10 right-5 sm:right-4 md:right-4 lg:right-8 xl:right-10 bg-green-500 text-white rounded-full p-2 lg:p-3 shadow-lg hover:bg-green-600 transition z-50 ${styles.whatsappBackground}`}
        >
          {/* WhatsApp Image Icon */}
          <img
            src={images.whatsapp_white}
            alt="WhatsApp Icon"
            className={`w-6 lg:w-7 h-auto ${styles.whatsappIcon}`}
          />
        </a>
      </section>

      {/* Certification Section */}
      <section
        id="bottom"
        className="relative font py-12 md:py-20 2xl:py-28 px-2 sm:px-4 md:px-8 lg:px-8 xl:px-16 2xl:px-16 bg-no-repeat bg-cover"
      >
        <img
          src={images.about_mobile_bg}
          alt="Background"
          className={`absolute object-cover xl:hidden lg:hidden md:hidden sm:block bottom-3/4 z-0 bg-cover ${styles.background}`}
        />
        <img
          src={images.about}
          alt={'Vector'}
          className="absolute object-cover hidden md:block right-0 top-48 lg:top-40 xl:top-36 bg-cover pt-24 2xl:pt-28 z-10"
        />
        <div className="absolute inset-0 z-10"></div>
        <div className="relative flex flex-col">
          <h1 className="title font-bold text-4xl 2xl:text-5xl items-center md:text-center pb-6">
            {t('certification.certificationText')}
          </h1>
          <p
            className="font font-normal text-base 2xl:text-lg md:text-center"
            style={{ fontFamily: "'Nunito Sans', sans-serif", fontWeight: 400 }}
          >
            {t('certification.certificationPara')}
          </p>
          <div
            className={`relative flex flex-col md:flex-row justify-center items-center md:space-x-5 2xl:space-x-10 pt-10 z-20`}
          >
            {certificate.map((certificate, index) => (
              <div
                key={index}
                className="flex flex-col justify-center space-x-2"
              >
                <img
                  src={certificate.image}
                  alt={t(certificate.name)}
                  className={`flex w-full mb-4 object-cover`}
                />
                <h3
                  className={`font font-normal text-base 2xl:text-lg text-center items-center pb-4 md:pb-0 ${styles.name}`}
                  style={{
                    fontFamily: "'Nunito Sans', sans-serif",
                    fontWeight: 400,
                  }}
                >
                  {t(certificate.name)}
                </h3>
              </div>
            ))}
          </div>
        </div>
        {/* Scroll to Top Button */}
        {showButton && (
          <div className="fixed xl:top-1/3 lg:top-2/4 md:top-1/4 top-1/3 right-0 transform z-50 ">
            <div className="py-8 px-3 md:py-6 md:px-6 lg:py-7 xl:py-10 xl:px-16 "></div>
            <div
              className="absolute flex flex-col lg:-right-4 xl:right-0 translate-y-1/2 hidden lg:flex  "
              onClick={scrollToTop}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={images.up}
                alt="UpArrow"
                className={`flex justify-center xl:pb-8 xl:h-16 lg:h-12 lg:pb-6 ${styles.icon}`}
              />
              <button
                onClick={scrollToTop}
                className={`flex text-sm md:text-lg lg:text-sm xl:text-base 2xl:text-lg text-red-500 rotate-90 transition duration-300 ${styles.top}`}
              >
                {t('whoWeAre.top')}
              </button>
            </div>
          </div>
        )}
      </section>
      {/* Map Section */}

      <Map />

      {/* Footer Section */}

      <Footer />
    </>
  );
};

export default Certification;
